import { useEffect, useRef, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import {
  SquaresFour, CheckCircle, CalendarBlank, ListChecks, Wallet, Heartbeat, SignOut, Lightning, ChatsCircle, MapPin, DotsThreeCircle, Atom, ForkKnife, Flask, MagnifyingGlass, Robot, FolderOpen,
} from "@phosphor-icons/react";
import useLocationShare from "@/hooks/useLocationShare";
import { ThemeSwitcher } from "@/components/ThemeSwitcher";
import { VoiceFab } from "@/components/VoiceFab";

const nav = [
  { to: "/", label: "Обзор", icon: SquaresFour, id: "nav-dashboard", end: true },
  { to: "/habits", label: "Привычки", icon: CheckCircle, id: "nav-habits" },
  { to: "/calendar", label: "Календарь", icon: CalendarBlank, id: "nav-calendar" },
  { to: "/planner", label: "Планнер", icon: ListChecks, id: "nav-planner" },
  { to: "/budget", label: "Бюджет", icon: Wallet, id: "nav-budget" },
  { to: "/health", label: "Здоровье", icon: Heartbeat, id: "nav-health" },
  { to: "/chat", label: "Семья", icon: ChatsCircle, id: "nav-chat" },
  { to: "/map", label: "Карта", icon: MapPin, id: "nav-map" },
  { to: "/assistant", label: "Файлообменник", icon: FolderOpen, id: "nav-assistant" },
  { to: "/twin", label: "Двойник", icon: Atom, id: "nav-twin" },
  { to: "/nutrition", label: "Питание", icon: ForkKnife, id: "nav-nutrition" },
  { to: "/labs", label: "Анализы", icon: Flask, id: "nav-labs" },
  { to: "/osint", label: "Разведка", icon: MagnifyingGlass, id: "nav-osint" },
  { to: "/ai", label: "AI", icon: Robot, id: "nav-ai" },
];

const mobileNav = [
  { to: "/chat", label: "Семья", icon: ChatsCircle, id: "nav-chat" },
  { to: "/health", label: "Здоровье", icon: Heartbeat, id: "nav-health" },
  { to: "/nutrition", label: "Питание", icon: ForkKnife, id: "nav-nutrition" },
  { to: "/ai", label: "AI", icon: Robot, id: "nav-ai" },
];

const moreNav = [
  { to: "/", label: "Обзор", icon: SquaresFour, id: "nav-dashboard", end: true },
  { to: "/habits", label: "Привычки", icon: CheckCircle, id: "nav-habits" },
  { to: "/calendar", label: "Календарь", icon: CalendarBlank, id: "nav-calendar" },
  { to: "/planner", label: "Планнер", icon: ListChecks, id: "nav-planner" },
  { to: "/budget", label: "Бюджет", icon: Wallet, id: "nav-budget" },
  { to: "/map", label: "GPS", icon: MapPin, id: "nav-map" },
  { to: "/assistant", label: "Файлы", icon: FolderOpen, id: "nav-assistant" },
  { to: "/twin", label: "Двойник", icon: Atom, id: "nav-twin" },
  { to: "/labs", label: "Анализы", icon: Flask, id: "nav-labs" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const mainRef = useRef(null);
  const { pathname } = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  useLocationShare();

  useEffect(() => {
    mainRef.current?.scrollTo(0, 0);
    setMoreOpen(false);
  }, [pathname]);

  useEffect(() => {
    let alive = true;
    const load = () => api.get("/chat/unread-total").then((r) => alive && setUnread(r.data.total || 0)).catch(() => {});
    load();
    const iv = setInterval(load, 25000);
    window.addEventListener("chat-read", load);
    return () => { alive = false; clearInterval(iv); window.removeEventListener("chat-read", load); };
  }, [pathname]);

  const badge = unread > 99 ? "99+" : unread;

  return (
    <div className="app-shell bg-background flex flex-col lg:flex-row overflow-hidden">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-60 shrink-0 border-r border-white/[0.07] flex-col fixed inset-y-0 left-0 glass-panel z-20">
        <div className="p-6 flex items-center gap-2 border-b border-border/60">
          <Lightning size={22} weight="fill" className="text-primary" />
          <span className="font-heading font-bold text-lg tracking-tight">LifeOS</span>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {nav.map(({ to, label, icon: Icon, id, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={id}
              className={({ isActive }) =>
                `relative flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                  isActive
                    ? "bg-primary/15 text-primary font-semibold shadow-[inset_0_0_0_1px_hsl(var(--primary)/0.25),0_0_16px_-4px_hsl(var(--primary)/0.5)]"
                    : "text-muted-foreground hover:text-foreground hover:bg-white/[0.04]"
                }`
              }
            >
              <Icon size={19} weight="duotone" />
              {label}
              {id === "nav-chat" && unread > 0 && (
                <span data-testid="chat-unread-badge" className="ml-auto min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold num flex items-center justify-center">
                  {badge}
                </span>
              )}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-border/60">
          <div className="mb-3 flex items-center justify-between">
            <span className="text-[11px] uppercase tracking-widest text-muted-foreground">Тема</span>
            <ThemeSwitcher />
          </div>
          <div className="text-sm font-medium truncate" data-testid="user-name">{user?.name}</div>
          <div className="text-xs text-muted-foreground truncate mb-3">{user?.email}</div>
          <button
            onClick={logout}
            data-testid="logout-button"
            className="flex items-center gap-2 text-xs text-muted-foreground hover:text-destructive transition-colors"
          >
            <SignOut size={16} /> Выйти
          </button>
        </div>
      </aside>

      {/* Mobile top header */}
      <header className="lg:hidden shrink-0 z-30 flex items-center gap-2 px-4 py-3 border-b border-white/[0.07] glass-panel pt-[max(0.75rem,env(safe-area-inset-top))]" data-testid="mobile-header">
        <Lightning size={20} weight="fill" className="text-primary" />
        <span className="font-heading font-bold tracking-tight">LifeOS</span>
        <span className="ml-auto text-xs text-muted-foreground truncate max-w-[40vw]" data-testid="mobile-user-name">{user?.name}</span>
        <button onClick={logout} data-testid="mobile-logout-button"
          className="p-1.5 rounded-md text-muted-foreground hover:text-destructive transition-colors">
          <SignOut size={18} />
        </button>
      </header>

      <main ref={mainRef} className="flex-1 lg:ml-60 min-h-0 overflow-y-auto overscroll-contain p-4 pb-8 sm:p-6 lg:p-10 min-w-0" data-testid="app-scroll-area">
        <Outlet />
      </main>

      <VoiceFab />

      {/* Mobile "Ещё" sheet */}
      {moreOpen && (
        <div className="lg:hidden fixed inset-0 z-40" data-testid="more-sheet" onClick={() => setMoreOpen(false)}>
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
          <div className="absolute bottom-0 left-0 right-0 rounded-t-2xl glass-panel border-t border-white/[0.1] p-4 pb-[calc(env(safe-area-inset-bottom)+76px)]"
            onClick={(e) => e.stopPropagation()}>
            <div className="w-10 h-1 rounded-full bg-white/20 mx-auto mb-4" />
            <div className="grid grid-cols-3 gap-2">
              {moreNav.map(({ to, label, icon: Icon, id, end }) => (
                <NavLink key={to} to={to} end={end} data-testid={`more-${id}`} onClick={() => setMoreOpen(false)}
                  className={({ isActive }) =>
                    `flex flex-col items-center gap-1.5 rounded-xl py-3.5 text-[11px] transition-colors ${
                      isActive ? "bg-primary/15 text-primary font-semibold" : "bg-white/[0.04] text-muted-foreground"
                    }`}>
                  {({ isActive }) => (
                    <>
                      <Icon size={22} weight={isActive ? "fill" : "duotone"} />
                      <span className="leading-none">{label}</span>
                    </>
                  )}
                </NavLink>
              ))}
            </div>
            <div className="mt-4 flex items-center justify-between px-1">
              <span className="text-xs text-muted-foreground">Тема оформления</span>
              <ThemeSwitcher />
            </div>
          </div>
        </div>
      )}

      {/* Mobile bottom nav — static flex child, always pinned */}
      <nav className="lg:hidden shrink-0 z-50 border-t border-white/[0.07] glass-panel pb-[env(safe-area-inset-bottom)]" data-testid="mobile-bottom-nav">
        <div className="grid grid-cols-5">
          {mobileNav.map(({ to, label, icon: Icon, id, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={`mobile-${id}`}
              className={({ isActive }) =>
                `relative flex flex-col items-center gap-0.5 py-2 text-[9px] transition-colors ${
                  isActive ? "text-primary font-semibold" : "text-muted-foreground"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {isActive && <span className="absolute top-0 h-0.5 w-8 rounded-full bg-primary shadow-[0_0_10px_2px_hsl(var(--primary)/0.6)]" />}
                  <span className="relative">
                    <Icon size={21} weight={isActive ? "fill" : "duotone"} />
                    {id === "nav-chat" && unread > 0 && (
                      <span data-testid="mobile-chat-unread-badge" className="absolute -top-1 -right-2.5 min-w-[16px] h-[16px] px-0.5 rounded-full bg-red-500 text-white text-[9px] font-bold num flex items-center justify-center border border-background">
                        {badge}
                      </span>
                    )}
                  </span>
                  <span className="leading-none">{label}</span>
                </>
              )}
            </NavLink>
          ))}
          <button onClick={() => setMoreOpen((o) => !o)} data-testid="mobile-nav-more"
            className={`relative flex flex-col items-center gap-0.5 py-2 text-[9px] transition-colors ${
              moreOpen || moreNav.some((n) => (n.end ? pathname === n.to : pathname.startsWith(n.to) && n.to !== "/"))
                ? "text-primary font-semibold" : "text-muted-foreground"}`}>
            <DotsThreeCircle size={21} weight={moreOpen ? "fill" : "duotone"} />
            <span className="leading-none">Ещё</span>
          </button>
        </div>
      </nav>
    </div>
  );
}
